import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { z } from 'zod';
import {
  Caption1,
  Checkbox,
  Field,
  Input,
  MessageBar,
  MessageBarBody,
  Text,
  Title3,
  makeStyles,
} from '@fluentui/react-components';
import { getStepType, hashControlledText, type ConfigOf } from '@vigeo/shared';
import { tokens } from '@vigeo/ui';
import type { StepPortalProps, StepReviewProps, StepUiModule } from '../contract.js';

const useStyles = makeStyles({
  block: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalM },
  // The words themselves. Scrollable so "I have read this" is a claim the person could
  // actually have made, rather than one the layout made impossible to check.
  text: {
    maxHeight: '22rem',
    overflowY: 'auto',
    whiteSpace: 'pre-wrap',
    padding: tokens.spacingVerticalM,
    borderRadius: tokens.borderRadiusMedium,
    border: `${tokens.strokeWidthThin} solid ${tokens.colorNeutralStroke2}`,
    backgroundColor: tokens.colorNeutralBackground2,
  },
  // 16px minimum or iOS Safari zooms the page on focus and the person loses the rest.
  name: { fontSize: tokens.fontSizeBase300 },
});

const kernel = getStepType('acknowledgment');
type Config = ConfigOf<'acknowledgment'>;

/** Sub-pixel layout means the arithmetic rarely lands exactly on zero. */
const SCROLL_SLACK = 4;

/**
 * Has the reader reached the end of the words?
 *
 * True when there is nothing to scroll, which is the case this got wrong. The gate was only
 * ever computed inside `onScroll`, so a short acknowledgment — one that fits the panel and
 * therefore fires no scroll event — left the confirmation permanently disabled and the
 * candidate unable to sign at all. The shortest, most common policies were the ones that
 * could never be completed.
 */
export function isAtEnd(box: { scrollHeight: number; scrollTop: number; clientHeight: number }): boolean {
  return box.scrollHeight - box.scrollTop - box.clientHeight < SCROLL_SLACK;
}

const draftSchema = z
  .object({ typedName: z.string(), agreedAt: z.string(), attestedTextHash: z.string(), read: z.boolean() })
  .partial();

function AcknowledgmentPortal({ config, draft, onDraftChange, disabled }: StepPortalProps) {
  const styles = useStyles();
  const scroller = useRef<HTMLDivElement | null>(null);
  const [scrolledToEnd, setScrolledToEnd] = useState(false);

  /**
   * Measured from LAYOUT, not from an event.
   *
   * Recomputed after render and whenever the box resizes — a phone rotating, the keyboard
   * opening, or a font finally loading all change whether there is anything left to scroll.
   * Once true it stays true: having read to the end is not undone by the box growing.
   */
  const measure = useCallback(() => {
    const box = scroller.current;
    if (box !== null && isAtEnd(box)) setScrolledToEnd(true);
  }, []);

  const [textHash, setTextHash] = useState<string | null>(null);

  const parsed = kernel.configSchema.safeParse(config);
  const settings: Config | null = parsed.success ? parsed.data : null;

  // Hash the PINNED text this component was handed. The server compares it to the sealed
  // digest and refuses a mismatch, which is what catches a policy edited while somebody had
  // it open — they would otherwise sign version 3 against a record saying version 4.
  const controlledText = settings?.controlledText ?? null;

  useLayoutEffect(() => {
    measure();
    const box = scroller.current;
    // ResizeObserver is absent in some test environments and older Safari; the mount-time
    // measurement above is what actually fixes the bug, and this only keeps it honest
    // afterwards.
    if (box === null || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(() => measure());
    observer.observe(box);
    return () => observer.disconnect();
  }, [measure, controlledText]);
  useEffect(() => {
    if (controlledText === null) return;
    let cancelled = false;
    void hashControlledText(controlledText).then((h) => {
      if (!cancelled) setTextHash(h);
    });
    return () => {
      cancelled = true;
    };
    // The TEXT is the dependency, not the parsed object — `safeParse` returns a fresh object
    // every render, so depending on it would re-hash on every keystroke.
  }, [controlledText]);

  if (settings === null) return <Text>This step is misconfigured. HR has been notified.</Text>;

  const current = draftSchema.safeParse(draft ?? {});
  const value = current.success ? current.data : {};
  const confirmed = value.read === true;

  function set(next: Partial<z.infer<typeof draftSchema>>) {
    const merged = { ...value, ...next };
    // A complete submission only once BOTH halves exist. The runner enables its button by
    // parsing the draft against the kernel's submissionSchema, so an incomplete draft simply
    // is not submittable — no separate rule to keep in step.
    onDraftChange(
      merged.read === true && (merged.typedName ?? '').trim() !== '' && textHash !== null
        ? {
            typedName: (merged.typedName ?? '').trim(),
            agreedAt: new Date().toISOString(),
            attestedTextHash: textHash,
            read: true,
          }
        : merged,
    );
  }

  return (
    <div className={styles.block}>
      <Title3>{settings.heading}</Title3>

      <div
        className={styles.text}
        ref={scroller}
        tabIndex={0}
        onScroll={(e) => {
          if (isAtEnd(e.currentTarget)) setScrolledToEnd(true);
        }}
      >
        <Text>{settings.controlledText}</Text>
      </div>

      {scrolledToEnd ? null : <Caption1>Scroll to the end to continue.</Caption1>}

      <Checkbox
        label={settings.signatureLabel}
        checked={confirmed}
        disabled={disabled || !scrolledToEnd}
        onChange={(_, d) => set({ read: d.checked === true })}
      />

      <Field label="Type your full name" hint="Typing your name here is your signature.">
        <Input
          className={styles.name}
          value={value.typedName ?? ''}
          disabled={disabled || !confirmed}
          autoComplete="name"
          onChange={(_, d) => set({ typedName: d.value })}
        />
      </Field>
    </div>
  );
}

function AcknowledgmentReview({ config, submission }: StepReviewProps) {
  const styles = useStyles();
  const settings = kernel.configSchema.safeParse(config);
  const answer = kernel.submissionSchema.safeParse(submission);

  return (
    <div className={styles.block}>
      <Text weight="semibold">{settings.success ? settings.data.heading : 'Acknowledgment'}</Text>
      {answer.success ? (
        <>
          <Text>
            Signed by {answer.data.typedName} on {new Date(answer.data.agreedAt).toLocaleString()}
          </Text>
          {/* The digest of the exact words, so "prove they saw this text" is answerable. */}
          <Caption1>Text digest {answer.data.attestedTextHash.slice(0, 23)}…</Caption1>
        </>
      ) : (
        <MessageBar intent="info">
          <MessageBarBody>Nothing signed yet.</MessageBarBody>
        </MessageBar>
      )}
      {settings.success ? <div className={styles.text}>{settings.data.controlledText}</div> : null}
    </div>
  );
}

export const acknowledgmentUi: StepUiModule = { portal: AcknowledgmentPortal, review: AcknowledgmentReview };
